"use client";

import React from "react";
import { House, ShieldCheck, ClipboardList, Key, Info } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const scenarios = [{
  title: "Older Properties",
  description: "Homes that are 20 to 25 years old or older frequently require this inspection to assess the condition and safety of aging major systems.",
  icon: House
}, {
  title: "Insurance Requirements",
  description: "Insurance carriers often mandate a 4-point report before issuing a new policy or renewing an existing one on older homes to gauge risk.",
  icon: ShieldCheck
}, {
  title: "Pre-Listing Preparation",
  description: "Sellers can proactively identify and address potential system issues before listing, ensuring a smoother transaction and fewer surprises.",
  icon: ClipboardList
}, {
  title: "Property Transactions",
  description: "Buyers gain essential insights into the current state of the roof, electrical, plumbing, and HVAC systems before finalizing their purchase.",
  icon: Key
}];
export default function WhenIsA4PointInspectionNeeded() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const footerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="when-is-a-4-point-inspection-needed" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Section Header */}
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight mb-4">
            When Is a 4-Point Inspection Needed?
          </h2>
          <p className="text-lg text-muted-foreground">
            Understanding the key scenarios where a 4-point inspection provides essential insights for your property's major systems.
          </p>
        </div>

        {/* Scenarios Grid */}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 transition-all duration-700 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {scenarios.map((scenario, i) => {
          const Icon = scenario.icon;
          return <Card key={i} data-index={i} className="h-full border-border shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardHeader className="pb-4">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary">
                    <Icon className="size-6" />
                  </div>
                  <CardTitle className="text-xl font-default">{scenario.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {scenario.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Insurance Note & CTAs */}
        <div ref={footerReveal.ref} className={`mt-12 md:mt-16 flex flex-col items-center text-center transition-all duration-700 delay-400 ease-out ${footerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-accent/50 rounded-xl p-6 max-w-4xl w-full border border-border/50 mb-8 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-left">
            <Info className="size-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold font-default text-foreground mb-1">Important Note on Insurance</h4>
              <p className="text-sm text-muted-foreground">
                While a 4-point inspection provides valuable, practical information about the condition of your property's systems and is often required by carriers, it does not guarantee insurance coverage, specific premium rates, or policy renewals.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="font-semibold">
              <Link to="https://trusted-fl.com/#schedule-your-inspection">Schedule an Inspection</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="https://trusted-fl.com/#our-inspection-services">Learn More About Our Services</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}